/**
 * Spatial / local-knowledge model — pure. Seeded features (hydrants, static
 * water, access notes, exposures) carry provenance + capture time so the UI can
 * attribute them and warn on staleness, and the deterministic rules can derive
 * water/access context from them rather than echoing manual input.
 */

import type { Confidence } from './fact.js';
import { type LngLat, nearest, within } from './geo.js';

export const FEATURE_KINDS = ['hydrant', 'static-water', 'access', 'exposure', 'local-note'] as const;
export type FeatureKind = (typeof FEATURE_KINDS)[number];

export type SpatialProvenance = 'seeded-osm' | 'seeded-gov' | 'synthetic' | 'manual';

export interface SeededFeature {
  id: string;
  kind: FeatureKind;
  coord: LngLat;
  label: string;
  provenance: SpatialProvenance;
  /** Attribution string (e.g. "© OpenStreetMap contributors, ODbL"). */
  source?: string;
  /** ISO capture/last-verified time, for staleness display. */
  capturedAt?: string;
  /** Kind-specific extras (flow confidence, suitability, access detail…). */
  props?: Record<string, unknown>;
}

export interface LocalityDataset {
  id: string;
  name: string;
  /** Attribution lines surfaced in-app. */
  attribution: string[];
  features: SeededFeature[];
}

const byKind = (ds: LocalityDataset, kind: FeatureKind): SeededFeature[] =>
  ds.features.filter((f) => f.kind === kind);

function confidenceOf(f: SeededFeature): Confidence {
  const explicit = f.props?.confidence;
  if (explicit === 'high' || explicit === 'medium' || explicit === 'low') return explicit;
  // Synthetic stand-ins are inherently lower confidence than mapped open data.
  return f.provenance === 'synthetic' ? 'low' : 'medium';
}

export interface SpatialWaterContext {
  nearestHydrantDistanceM?: number;
  nearestHydrantLabel?: string;
  hydrantConfidence?: Confidence;
  staticWaterAvailable: boolean;
  nearestStaticWaterDistanceM?: number;
  nearestStaticWaterLabel?: string;
}

/** Derive water-supply context for an incident point from the seeded dataset. */
export function deriveWaterContext(
  incident: LngLat,
  dataset: LocalityDataset,
  staticWaterRadiusM = 2000,
): SpatialWaterContext {
  const h = nearest(incident, byKind(dataset, 'hydrant'), (f) => f.coord);
  const staticWater = within(incident, byKind(dataset, 'static-water'), staticWaterRadiusM, (f) => f.coord);
  const nearestStatic = staticWater[0];
  return {
    nearestHydrantDistanceM: h?.distanceM,
    nearestHydrantLabel: h?.item.label,
    hydrantConfidence: h ? confidenceOf(h.item) : undefined,
    staticWaterAvailable: staticWater.length > 0,
    nearestStaticWaterDistanceM: nearestStatic?.distanceM,
    nearestStaticWaterLabel: nearestStatic?.item.label,
  };
}

export interface NearbyNote {
  label: string;
  distanceM: number;
  kind: FeatureKind;
  provenance: SpatialProvenance;
  capturedAt?: string;
}

/** Access notes + local knowledge within `radiusM` of the incident, nearest first. */
export function localKnowledgeNear(
  incident: LngLat,
  dataset: LocalityDataset,
  radiusM = 500,
): NearbyNote[] {
  const candidates = dataset.features.filter((f) => f.kind === 'access' || f.kind === 'local-note');
  return within(incident, candidates, radiusM, (f) => f.coord).map((n) => ({
    label: n.item.label,
    distanceM: n.distanceM,
    kind: n.item.kind,
    provenance: n.item.provenance,
    capturedAt: n.item.capturedAt,
  }));
}

/** Exposures within `radiusM` of the incident, nearest first. */
export function exposuresNear(incident: LngLat, dataset: LocalityDataset, radiusM = 60): NearbyNote[] {
  return within(incident, byKind(dataset, 'exposure'), radiusM, (f) => f.coord).map((n) => ({
    label: n.item.label,
    distanceM: n.distanceM,
    kind: n.item.kind,
    provenance: n.item.provenance,
    capturedAt: n.item.capturedAt,
  }));
}
